import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "../stores/auth";

// Import views
import Login from "../views/auth/Login.vue";
import Register from "../views/auth/Register.vue";
import ForgotPassword from "../views/auth/ForgotPassword.vue";
import ResetPassword from "../views/auth/ResetPassword.vue";

// Admin views
import AdminDashboard from "../views/admin/Dashboard.vue";
import AdminSongs from "../views/admin/Songs.vue";
import AdminEvents from "../views/admin/Events.vue";
import EventForm from "../views/admin/EventForm.vue";
import AdminUsers from "../views/admin/Users.vue";
import AdminUserProfile from "../views/admin/UserProfile.vue";
import AdminPermissions from "../views/admin/Permissions.vue";
import AdminStats from "../views/admin/Stats.vue";
import AdminCategories from "../views/admin/Categories.vue";
import AdminGenres from "../views/admin/Genres.vue";

// Event views
import LiveEvent from "../views/events/Live.vue";

// Documentation views
import DashboardDoc from "../views/documentation/Dashboard.vue";
import EventsDoc from "../views/documentation/Events.vue";
import SongsDoc from "../views/documentation/Songs.vue";
import UsersDoc from "../views/documentation/Users.vue";
import StatisticsDoc from "../views/documentation/Statistics.vue";

const routes = [
  {
    path: "/",
    name: "AdminDashboard",
    component: AdminDashboard,
    meta: { requiresAuth: true }
  },
  {
    path: "/login",
    name: "Login",
    component: Login
  },
  {
    path: "/register",
    name: "Register",
    component: Register
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPassword
  },
  // Admin routes (clean URLs)
  {
    path: "/songs",
    name: "Songs",
    component: AdminSongs,
    meta: { requiresAuth: true }
  },
  {
    path: "/categories",
    name: "Categories",
    component: AdminCategories,
    meta: { requiresAuth: true }
  },
  {
    path: "/genres",
    name: "Genres",
    component: AdminGenres,
    meta: { requiresAuth: true }
  },
  {
    path: "/events",
    name: "Events",
    component: AdminEvents,
    meta: { requiresAuth: true }
  },
  {
    path: "/events/live/:eventId",
    name: "LiveEvent",
    component: LiveEvent,
    meta: { requiresAuth: true }
  },
  {
    path: "/events/live",
    name: "LiveEventPicker",
    component: LiveEvent,
    meta: { requiresAuth: true }
  },
  {
    path: "/events/new",
    name: "EventCreate",
    component: EventForm,
    meta: { requiresAuth: true }
  },
  {
    path: "/events/:id/edit",
    name: "EventEdit",
    component: EventForm,
    meta: { requiresAuth: true }
  },
  {
    path: "/users",
    name: "Users",
    component: AdminUsers,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: "/users/permissions",
    name: "Permissions",
    component: AdminPermissions,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: "/users/:id",
    name: "UserProfile",
    component: AdminUserProfile,
    meta: { requiresAuth: true }
  },
  {
    path: "/stats",
    name: "Stats",
    component: AdminStats,
    meta: { requiresAuth: true }
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("../views/admin/Settings.vue"),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: "/overlays/templates",
    name: "OverlayTemplates",
    component: () => import("../views/overlays/Templates.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/overlays/templates/new",
    name: "OverlayTemplateNew",
    component: () => import("../views/overlays/TemplateEditor.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/overlays/templates/:id",
    name: "OverlayTemplateEdit",
    component: () => import("../views/overlays/TemplateEditor.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/documentation/dashboard",
    name: "DashboardDocumentation",
    component: DashboardDoc,
    meta: { requiresAuth: true }
  },
  {
    path: "/documentation/events",
    name: "EventsDocumentation",
    component: EventsDoc,
    meta: { requiresAuth: true }
  },
  {
    path: "/documentation/songs",
    name: "SongsDocumentation",
    component: SongsDoc,
    meta: { requiresAuth: true }
  },
  {
    path: "/documentation/users",
    name: "UsersDocumentation",
    component: UsersDoc,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: "/documentation/statistics",
    name: "StatisticsDocumentation",
    component: StatisticsDoc,
    meta: { requiresAuth: true }
  }
  // Event routes removed - admin only
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // If we have a token but no user, fetch the profile first
  if (authStore.token && !authStore.user) {
    try {
      await authStore.fetchProfile();
    } catch (error) {
      // If fetch fails, token is invalid - clear it and redirect to login
      authStore.logout();
      if (to.meta.requiresAuth) {
        next("/login");
        return;
      }
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next("/login");
    return;
  }

  const ownProfile =
    to.name === "UserProfile" && String(to.params.id) === String(authStore.user?.id);
  const isAdmin = authStore.user?.role === "admin" || authStore.user?.role === "owner";

  // Helper users can access the live show and their own profile.
  if (authStore.user?.role === "helper") {
    const isLivePath = to.path === "/events/live" || to.path.startsWith("/events/live/");
    if (!isLivePath && !ownProfile) {
      next("/events/live");
      return;
    }
    next();
    return;
  }

  if (to.name === "UserProfile" && !ownProfile && !isAdmin) {
    next("/");
    return;
  }

  // Check if route requires admin privileges (owners also have admin access)
  if (
    to.meta.requiresAdmin &&
    authStore.user?.role !== "admin" &&
    authStore.user?.role !== "owner"
  ) {
    next("/login");
    return;
  }

  next();
});

export default router;
